import { gql } from '@apollo/client';

import { SearchStatus } from '../../state';

/**
 * GraphQL queries for job searches sourcing stats and progress counts
 * These replace the old REST API endpoint /searches/job/:jobId/stats
 */

/**
 * Query to fetch cached sourcing statistics for all searches in a job
 * Uses the cache table for fast retrieval (<10ms vs 2-10 seconds for live query)
 * Note: Aliases operation and field names for cleaner TypeScript usage
 */
export const JOB_SEARCH_SOURCING_STATS_CACHE = gql`
    query JobSearchSourcingStatsCache($where: job_search_sourcing_stats_cache_bool_exp!) {
        searchStats: job_search_sourcing_stats_cache(where: $where) {
            id: searchId
            ready
            lowScore
            noScore
            added
            alreadyAdded
            blocked
            inCooldown
            findingEmail
            expiredProfile
            rejected
            unknownStatus
            totalNew
            refreshedAt
        }
    }
`;

/**
 * Query to fetch progress counts (candidates by stage) for all searches in a job
 * Calls the PostgreSQL function directly - not cached but acceptable performance
 * Note: Aliases operation name for cleaner TypeScript usage
 */
export const JOB_SEARCH_PROGRESS_COUNTS = gql`
    query JobSearchProgressCounts($jobId: String!) {
        progressCounts: job_search_candidate_stage_counts(args: { job_id: $jobId }) {
            searchId
            sourcedBy
            counts
        }
    }
`;

/**
 * Mutation to manually refresh the sourcing stats cache for a specific job/user
 * Useful when user wants to see latest data immediately
 * Note: Aliases operation and field names for cleaner TypeScript usage
 */
export const REFRESH_JOB_SEARCH_SOURCING_STATS_CACHE = gql`
    mutation RefreshJobSearchSourcingStatsCache($jobId: String!, $userId: String) {
        refreshCache: refresh_job_search_sourcing_stats_cache(args: { job_id: $jobId, user_id: $userId }) {
            id: searchId
            ready
            lowScore
            noScore
            added
            alreadyAdded
            blocked
            inCooldown
            findingEmail
            expiredProfile
            rejected
            unknownStatus
            totalNew
            refreshedAt
        }
    }
`;

/**
 * TypeScript interfaces for query results
 * Note: Interfaces match the aliased GraphQL field names
 */

export interface JobSearchSourcingStats {
    id: string;
    ready: number;
    lowScore: number;
    noScore: number;
    added: number;
    alreadyAdded: number;
    blocked: number;
    inCooldown: number;
    findingEmail: number;
    expiredProfile: number;
    rejected: number;
    unknownStatus: number;
    totalNew: number;
    refreshedAt: number;
}

export interface JobSearchProgressCount {
    searchId: string | null;
    sourcedBy: 'ai_sourced' | 'recruiter_sourced';
    counts: Array<{
        stage: string;
        count: number;
    }>;
}

export interface JobSearchSourcingStatsCacheData {
    searchStats: JobSearchSourcingStats[];
}

export interface JobSearchProgressCountsData {
    progressCounts: JobSearchProgressCount[];
}

export interface RefreshJobSearchSourcingStatsCacheData {
    refreshCache: JobSearchSourcingStats[];
}

/**
 * Job searches list queries
 * These queries are used for displaying the searches table/list on the job searches page
 */

export interface JobSearch {
    id: string;
    status: SearchStatus;
    searchProfilesScoreId: string;
}

// Lightweight subscription to know when searches change
export const JOB_SEARCHES_SUB = gql`
    subscription JobSearchesSub($where: searches_bool_exp!) {
        searches(where: $where) {
            id
            jobId
            status
            reviewRequiredForOutreach
            searchProfilesScoreId
            sourcingStats {
                refreshedAt
            }
        }
    }
`;

// Full query to fetch all search data needed for rendering the table
export const JOB_SEARCHES = gql`
    query JobSearches($where: searches_bool_exp!) {
        searches(where: $where, order_by: { sortRank: asc }) {
            id
            importRequestId
            jobId
            name
            config
            querySql
            strategy
            agentSearch {
                id
                title
                counts
                conversationId
            }
            status
            userId
            createdBy
            createdAt
            sortRank
            project
            reviewRequiredForOutreach
            searchProfilesScoreId
        }
    }
`;

export const JOB_SEARCH_USERS = gql`
    query SearchUsers($jobId: String!) {
        searches(where: { jobId: { _eq: $jobId } }) {
            id
            status
            user {
                id
                name(path: "full")
            }
        }
    }
`;
